import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { PieChart } from "@mui/x-charts";
import SoothCard from "components/base/SoothCard";
import { colorArray } from "../data/color.data";
import AccessibleIconButton from "components/base/AccessibleIconButton";

// Helper: stable color by label/id, with optional domain for cross-chart consistency
const colorizeData = (data, colors, domain) => {
  if (!Array.isArray(data)) return [];
  const keys =
    domain && domain.length
      ? domain
      : Array.from(new Set(data.map((d) => d.label ?? d.id)));
  const map = new Map(keys.map((k, i) => [k, colors[i % colors.length]]));
  return data.map((d) => ({ ...d, color: map.get(d.label ?? d.id) }));
};

export function CommonPieChartOnly({
  data,
  height = 340,
  width,
  showLegend = true,
  innerRadius = 60,
  outerRadius,
  paddingAngle = 3,
  cornerRadius = 3,
  startAngle = -90,
  endAngle = 270,
  arcLabelMinAngle = 15,
  colors = colorArray,
  domain,
  valueFormatter,
  margin = { left: 0, top: 10, right: 0, bottom: 4 * 28.6 },
}) {
  const theme = useTheme();
  const colored = React.useMemo(
    () => colorizeData(data, colors, domain),
    [data, colors, domain]
  );

  return (
    <PieChart
      height={height}
      width={width}
      margin={margin}
      colors={colors}
      series={[
        {
          data: colored,
          innerRadius,
          outerRadius,
          paddingAngle,
          cornerRadius,
          startAngle,
          endAngle,
          arcLabelMinAngle,
          valueFormatter,
        },
      ]}
      slotProps={{
        legend: showLegend
          ? {
              direction: "column",
              position: {
                vertical: "bottom",
                horizontal: "left",
              },
              labelStyle: {
                fontSize: theme.typography.caption.fontSize,
              },
              itemMarkWidth: 20,
              itemMarkHeight: 2,
              markGap: 5,
              itemGap: 10,
            }
          : { hidden: true },
      }}
    />
  );
}

export default function CommonPieChart({
  title,
  data,
  height = 340,
  minHeight = "40vh",
  maxHeight = "70vh",
  width,
  showLegend = true,
  innerRadius = 60,
  outerRadius,
  paddingAngle = 3,
  cornerRadius = 3,
  startAngle = -90,
  endAngle = 270,
  arcLabelMinAngle = 15,
  colors = colorArray,
  domain,
  valueFormatter,
  margin = { left: 0, top: 10, right: 0, bottom: 4 * 28.6 },
  description,
}) {
  const theme = useTheme();

  return (
    <SoothCard
      sx={{
        maxHeight,
        minHeight,
        display: "flex",
        flexDirection: "column",
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography component="div" variant="h4" sx={{ textAlign: "center" }}>
          {title}
        </Typography>
        {description && (
          <AccessibleIconButton
            size="small"
            sx={{ p: 0.5 }}
            title={description}
            tooltipProps={{
              placement: "top",
            }}
          >
            <InfoOutlinedIcon fontSize="small" color="primary" />
          </AccessibleIconButton>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <CommonPieChartOnly
          data={data}
          height={height}
          width={width}
          showLegend={showLegend}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          paddingAngle={paddingAngle}
          cornerRadius={cornerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          arcLabelMinAngle={arcLabelMinAngle}
          colors={colors}
          domain={domain}
          valueFormatter={valueFormatter}
          margin={margin}
        />
      </Box>
    </SoothCard>
  );
}
